import React, { useState } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import ReactPlayer from 'react-player';
import { IChatMessage } from '../../../redux/chat/chatSlice';
import { extractTimeFromDate } from '../../../utils/dateUtils';
import { AssetType } from '../../../redux/activity/activitySlice';
import LetterAvatar from '../../LetterAvatar/LetterAvatar';
import VoiceMessage from './VoiceMessage';
import { RoundedSkeleton } from '../../Common/Skeleton';
import { IClientNameListItem } from '../../../types/client/IClientNameListItem';

interface IMessageProps {
  own?: boolean;
  message: IChatMessage;
  client: IClientNameListItem;
}

function Message({ own, message, client }: IMessageProps) {
  const [imageLoading, setImageLoading] = useState(true);
  const [documentLoading, setDocumentLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);

  const imageLoaded = () => {
    setImageLoading(false);
  };

  const documentLoaded = () => {
    setDocumentLoading(false);
  };

  const videoLoaded = () => {
    setVideoLoading(false);
  };

  return (
    <Grid
      gap="10px"
      display="flex"
      flexDirection="row"
      justifyContent={own ? 'flex-end' : 'flex-start'}
    >
      {!own && client?.profilePic && (
        <img
          src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${client?.profilePic}`}
          alt="user"
          width={36}
          height={36}
          style={{ borderRadius: '50%', objectFit: 'cover' }}
        />
      )}
      {!own && !client?.profilePic && (
        <LetterAvatar firstName={client.firstName} lastName={client.lastName ?? ''} size={36} />
      )}
      <Grid
        gap="4px"
        display="flex"
        flexDirection="column"
        alignItems={own ? 'flex-end' : 'flex-start'}
      >
        <Grid
          bgcolor={own ? '#FFDDC8' : 'rgba(124, 174, 190, 0.15)'}
          borderRadius={own ? '20px 0px 20px 20px' : '0px 20px 20px 20px'}
          p={2}
          maxWidth={own ? 520 : 540}
        >
          {message.messageType === AssetType.TEXT && (
            <Typography
              variant="bodyRegular"
              color="primary"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {message.message}
            </Typography>
          )}

          {message.messageType === AssetType.IMAGE && (
            <>
              <Box style={{ display: imageLoading ? 'block' : 'none' }} gap="4px">
                <RoundedSkeleton height={14.875} width={497} />
                <RoundedSkeleton height={14.875} width={464} />
                <RoundedSkeleton height={14.875} width={451} />
              </Box>
              <Box style={{ display: imageLoading ? 'none' : 'block' }}>
                <img
                  src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${message.message}`}
                  alt="user"
                  style={{ objectFit: 'cover', borderRadius: '10px' }}
                  width="100%"
                  height="100%"
                  onLoad={imageLoaded}
                />
              </Box>
            </>
          )}
          {message.messageType === AssetType.AUDIO && (
            <VoiceMessage message={message.message} totalDuration={message.assetDuration} />
          )}
          {message.messageType === AssetType.DOCUMENT && (
            <>
              <Box style={{ display: documentLoading ? 'block' : 'none' }} gap="4px">
                <RoundedSkeleton height={14.875} width={497} />
                <RoundedSkeleton height={14.875} width={464} />
                <RoundedSkeleton height={14.875} width={451} />
              </Box>
              <Box style={{ display: documentLoading ? 'none' : 'block' }}>
                <iframe
                  title={message.message}
                  src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${message.message}`}
                  width="100%"
                  height="100%"
                  onLoad={documentLoaded}
                />
              </Box>
            </>
          )}
          {message.messageType === AssetType.VIDEO && (
            <>
              <Box style={{ display: videoLoading ? 'block' : 'none' }} gap="4px">
                <RoundedSkeleton height={14.875} width={497} />
                <RoundedSkeleton height={14.875} width={464} />
                <RoundedSkeleton height={14.875} width={451} />
              </Box>
              <Box style={{ display: videoLoading ? 'none' : 'block' }}>
                <ReactPlayer
                  url={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${message.message}`}
                  controls
                  data-testid="demo-video-player"
                  width="100%"
                  height="100%"
                  onReady={videoLoaded}
                />
              </Box>
            </>
          )}
        </Grid>
        <Grid gap="4px" alignItems="center" display="flex">
          <Typography variant="tinyTexts" color="#7CAEBE" fontWeight={700}>
            {extractTimeFromDate(message.updatedAt)}
          </Typography>
          {own && (
            <Box height={32} display="flex" alignItems="center">
              {(() => {
                if (message.messageSeen) {
                  return (
                    <img
                      src="/assets/icons/check-seen.svg"
                      alt="message status"
                      height={20}
                      width={20}
                    />
                  );
                }
                if (message.messageDelivered) {
                  return (
                    <img
                      src="/assets/icons/check-sent.svg"
                      alt="message status"
                      height={20}
                      width={20}
                    />
                  );
                }
                return <div />;
              })()}
            </Box>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default Message;
